import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { PencilIcon, TrashIcon, PlusIcon, CheckIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import 'tailwindcss/tailwind.css';
import { saveAs } from 'file-saver';
import { Tooltip } from 'react-tooltip';
import Cookies from 'js-cookie';

const Checklist = () => {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [tasks, setTasks] = useState([{ _id: null, name: '', endDate: '', completed: false, notes: '' }]);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [operationLoading, setOperationLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteChecklistId, setDeleteChecklistId] = useState(null);

  useEffect(() => {
    fetchChecklists();
  }, []);

  const fetchChecklists = async () => {
    setLoading(true);
    try {
      const user = JSON.parse(Cookies.get("user"));
      const userId = user._id;
      const response = await axios.get(`${process.env.REACT_APP_ADDRESS}/checklists/${userId}/fetchAll`, {
        headers: { Authorization: `Bearer ${Cookies.get('token')}` },
      });
      setChecklists(response.data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setTasks([{ _id: null, name: '', endDate: '', completed: false, notes: '' }]);
    setSelectedChecklist(null);
    setFieldErrors({});
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options = { month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const handleChecklistSelect = (checklist) => {
    setSelectedChecklist(checklist);
    setName(checklist.name);
    setTasks(checklist.tasks.map(task => ({
      ...task,
      endDate: formatDate(task.endDate),
    })));
  };

  const handleTaskChange = (index, field, value) => {
    const newTasks = [...tasks];
    newTasks[index][field] = value;
    setTasks(newTasks);
  };

  const addTask = () => {
    setTasks([...tasks, { _id: null, name: '', endDate: '', completed: false, notes: '' }]);
  };

  const deleteTask = async (taskId, index) => {
    setOperationLoading(true);
    if (!taskId) {
      setTasks(tasks.filter((_, i) => i !== index));
      setOperationLoading(false);
      return;
    }
    try {
      await axios.put(`${process.env.REACT_APP_ADDRESS}/checklists/${selectedChecklist._id}/task`, {
        taskId,
        action: 'remove',
      }, {
        headers: { Authorization: `Bearer ${Cookies.get('token')}` },
      });
      const updatedTasks = selectedChecklist.tasks.filter((task) => task._id !== taskId);
      setSelectedChecklist({ ...selectedChecklist, tasks: updatedTasks });
      setTasks(updatedTasks);
    } catch (error) {
      setError(error.message);
    } 
  };

  const validateForm = () => {
    const errors = {};
    if (!name) errors.name = "Checklist name is required.";
    tasks.forEach((task, index) => {
      if (!task.name) errors[`task-${index}-name`] = "Task name is required.";
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setOperationLoading(true);
    if (!validateForm()) {
      setOperationLoading(false);
      return;
    }

    try {
      const user = JSON.parse(Cookies.get("user"));
      const response = await axios.post(
        `${process.env.REACT_APP_ADDRESS}/checklists/create/${user._id}`,
        { name, tasks: tasks.map(task => ({ ...task, _id: undefined })) },
        { headers: { Authorization: `Bearer ${Cookies.get('token')}` } }
      );
      setSuccessMessage('Checklist created successfully!');
      setChecklists([...checklists, response.data]);
      resetForm();
      setError(null);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleChecklistUpdate = async () => {
    setOperationLoading(true);
    if (!validateForm()) {
      setOperationLoading(false);
      return;
    }

    try {
      await axios.put(
        `${process.env.REACT_APP_ADDRESS}/checklists/${selectedChecklist._id}`,
        { name, tasks: tasks.map(task => ({ ...task, _id: task._id || undefined })) },
        { headers: { Authorization: `Bearer ${Cookies.get('token')}` } }
      );
      setSuccessMessage('Checklist updated successfully!');
      fetchChecklists();
      resetForm();
      setError(null);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleTaskCompleteToggle = async (checklistId, taskId) => {
    try {
      setChecklists(prevChecklists =>
        prevChecklists.map(checklist => {
          if (checklist._id === checklistId) {
            return {
              ...checklist,
              tasks: checklist.tasks.map(task => {
                if (task._id === taskId) {
                  const newCompletedStatus = !task.completed;
                  // Update the task on the server
                  axios.put(
                    `${process.env.REACT_APP_ADDRESS}/checklists/${checklistId}/task/${taskId}`,
                    { completed: newCompletedStatus },
                    { headers: { Authorization: `Bearer ${Cookies.get('token')}` } }
                  ).catch(error => {
                    console.error('Error updating task:', error);
                    setError('Failed to update task. Please try again.');
                  });
                  return { ...task, completed: newCompletedStatus };
                }
                return task;
              })
            };
          }
          return checklist;
        })
      );
      // Update tasks in selectedChecklist if it's the one being edited
      if (selectedChecklist && selectedChecklist._id === checklistId) {
        setTasks(prevTasks =>
          prevTasks.map(task => {
            if (task._id === taskId) {
              return { ...task, completed: !task.completed };
            }
            return task;
          })
        );
      }
    } catch (error) {
      console.error('Error in handleTaskCompleteToggle:', error);
      setError('An error occurred while updating the task. Please try again.');
    }
  };

  const confirmDeleteChecklist = (checklistId) => {
    setDeleteChecklistId(checklistId);
    setShowDeleteModal(true);
  };

  const deleteChecklist = async () => {
    if (!deleteChecklistId) return;
    setOperationLoading(true);
    try {
      await axios.delete(`${process.env.REACT_APP_ADDRESS}/checklists/${deleteChecklistId}`, {
        headers: { Authorization: `Bearer ${Cookies.get('token')}` },
      });
      setSuccessMessage('Checklist deleted successfully!');
      const updatedChecklists = checklists.filter((checklist) => checklist._id !== deleteChecklistId);
      setChecklists(updatedChecklists);
      resetForm();
      setShowDeleteModal(false);
      setDeleteChecklistId(null);
    } catch (error) {
      setError(error.message);
    } 
  };

  const calculateProgress = (tasks) => {
    const completedTasks = tasks.filter(task => task.completed).length;
    const totalTasks = tasks.length;
    return totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const newTasks = Array.from(tasks);
    const [movedTask] = newTasks.splice(result.source.index, 1);
    newTasks.splice(result.destination.index, 0, movedTask);
    setTasks(newTasks);
  };

  const downloadPDF = async (checklist) => {
    const docContent = { checklists: [checklist] };
    console.log('Downloading PDF for:', docContent);
    try {
      await axios.post(`${process.env.REACT_APP_ADDRESS}/checklists/createpdf`, docContent);
      const response = await axios.get(`${process.env.REACT_APP_ADDRESS}/checklists/fetchpdf`, { responseType: 'blob' });

      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      saveAs(pdfBlob, `${checklist.name}.pdf`);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  if (loading) return <div className="text-center text-gray-700">Loading...</div>;
  if (error) return <div className="text-center text-red-500">{error}</div>;

  return (
    <div className="p-8 max-w-5xl mx-auto bg-white min-h-screen">
      {successMessage && <div className="text-green-600 mb-4">{successMessage}</div>}
      {error && <div className="text-red-500 mb-4">{error}</div>}
      {operationLoading && <div className="text-center text-indigo-500">Processing...</div>}
      <div className="mb-8 flex justify-between items-center">
        <h2 className="text-3xl font-bold text-gray-700">Select a Checklist</h2>
        <button
          onClick={resetForm}
          className="bg-pink-500 text-white p-3 rounded-lg hover:bg-pink-600 flex items-center space-x-2"
        >
          <PlusIcon className="h-6 w-6" /> <span>New Checklist</span>
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8"
      data-tooltip-id="checklist"
      data-tooltip-content="your finished checklist"
      data-tooltip-float="true">
        {checklists.map((checklist) => (
          <button key={checklist._id} onClick={() => handleChecklistSelect(checklist)} className="bg-white shadow-lg p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200">
            {checklist.name}
          </button>
        ))}
      </div>
      <Tooltip id="checklist" />
      <form onSubmit={selectedChecklist ? handleChecklistUpdate : handleSubmit} className="mb-8 p-6 bg-white shadow-md rounded-lg">
        <h2 className="text-3xl font-bold text-gray-700 mb-4">{selectedChecklist ? "Update Your Checklist" : "Create a New Checklist"}</h2>
        <div className="mb-4">
          <label className="block text-gray-700 mb-2">Checklist Name</label>
          <input
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {fieldErrors.name && <div className="text-red-500 mt-2">{fieldErrors.name}</div>}
        </div>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="tasks">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {tasks.map((task, index) => (
                  <Draggable key={task._id || index} draggableId={task._id || index.toString()} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`bg-gray-50 mb-4 p-4 rounded-lg flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 ${task.completed ? 'line-through text-gray-500' : ''}`}
                      >
                        <EllipsisVerticalIcon className="h-6 w-6 text-gray-500 cursor-move mr-2" {...provided.dragHandleProps} />
                        <div className="relative w-full mb-2 md:mb-0 md:mr-2">
                          <label
                            htmlFor={`task-${index}-name`}
                            className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium text-gray-900"
                          >
                            Task Name
                          </label>
                          <input
                            id={`task-${index}-name`}
                            name={`task-${index}-name`}
                            type="text"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            value={task.name}
                            onChange={(e) => handleTaskChange(index, 'name', e.target.value)}
                          />
                          {fieldErrors[`task-${index}-name`] && <div className="text-red-500 mt-2">{fieldErrors[`task-${index}-name`]}</div>}
                        </div>
                        <div className="relative w-full mb-2 md:mb-0 md:mr-2">
                          <label
                            htmlFor={`task-${index}-endDate`}
                            className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium text-gray-900"
                          >
                            End Date
                          </label>
                          <input
                            id={`task-${index}-endDate`}
                            name={`task-${index}-endDate`}
                            type="date"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            value={task.endDate || ''}
                            onChange={(e) => handleTaskChange(index, 'endDate', e.target.value)}
                            placeholder="End Date"
                          />
                        </div>
                        <div className="relative w-full mb-2 md:mb-0 md:mr-2">
                          <label
                            htmlFor={`task-${index}-notes`}
                            className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium text-gray-900"
                          >
                            Notes
                          </label>
                          <input
                            id={`task-${index}-notes`}
                            name={`task-${index}-notes`}
                            type="text"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            value={task.notes}
                            onChange={(e) => handleTaskChange(index, 'notes', e.target.value)}
                          />
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={task.completed}
                            onChange={() => handleTaskCompleteToggle(selectedChecklist._id, task._id)}
                            className="mr-2 h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                          <button
                            type="button"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => deleteTask(task._id, index)}
                          >
                            <TrashIcon className="h-6 w-6" />
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <div className="flex justify-between">
          <button
            type="button"
            className="bg-indigo-500 text-white p-3 rounded-lg hover:bg-indigo-600 flex items-center space-x-2"
            onClick={addTask}
          >
            <PlusIcon className="h-6 w-6" /> <span>Add Task</span>
          </button>
          {selectedChecklist ? (
            <button
              type="button"
              className="bg-pink-500 text-white p-3 rounded-lg hover:bg-pink-600 flex items-center space-x-2"
              onClick={handleChecklistUpdate}
            >
              <CheckIcon className="h-6 w-6" /> <span>Update Checklist</span>
            </button>
          ) : (
            <button
              type="submit"
              className="bg-pink-500 text-white p-3 rounded-lg hover:bg-pink-600 flex items-center space-x-2"
            >
              <CheckIcon className="h-6 w-6" /> <span>Create Checklist</span>
            </button>
          )}
        </div>
      </form>
      {selectedChecklist && (
        <button
          onClick={() => downloadPDF(selectedChecklist)}
          className="bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600 flex items-center space-x-2 mb-4"
        >
          <CheckIcon className="h-6 w-6" /> <span>Download PDF</span>
        </button>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {checklists.map((checklist) => (
          <div key={checklist._id} className="bg-white shadow-lg p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-gray-700 mb-4">{checklist.name}</h2>
            <ul className="list-none p-0">
              {checklist.tasks.map((task) => (
                <li
                  key={task._id}
                  className={`bg-gray-100 mb-2 p-4 rounded flex justify-between items-center ${task.completed ? 'line-through text-gray-500' : 'text-gray-700'}`}
                >
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={task.completed}
                      onChange={() => handleTaskCompleteToggle(checklist._id, task._id)}
                      className="mr-2 h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      data-tooltip-id="checkbox"
                      data-tooltip-content="check the box if it's finished"
                      data-tooltip-float="true"
                    />
                    <Tooltip id="checkbox"/>
                    <span>{task.name}</span>
                  </div>
                  <span>{formatDate(task.endDate)}</span>
                </li>
              ))}
            </ul>
            <div className="mb-4">
              <label className="block text-gray-700 mb-2">Progress</label>
              <div className="w-full bg-gray-200 rounded-full"
              data-tooltip-id="progress"
              data-tooltip-content="Track your progress"
              data-tooltip-float="true">
                <div
                  className="bg-pink-500 text-xs leading-none py-1 text-center text-white rounded-full"
                  style={{ width: `${calculateProgress(checklist.tasks)}%` }}
                  
                >
                  {Math.round(calculateProgress(checklist.tasks))}%
                </div>
                <Tooltip id="progress" />
              </div>
            </div>
            <div className="flex justify-between">
              <button
                data-tooltip-id="deleteChecklist"
                className="text-red-600 hover:text-red-700"
                onClick={() => confirmDeleteChecklist(checklist._id)}
                data-tooltip-content="Delete This Checklist"
                data-tooltip-float="true"

              >
                <TrashIcon className="h-6 w-6" />
              </button>
              <Tooltip id="deleteChecklist" />
              <button
                className="text-indigo-500 hover:text-indigo-700"
                onClick={() => handleChecklistSelect(checklist)}
                data-tooltip-id="EditChecklist"
                data-tooltip-content="Edit This Checklist"
                data-tooltip-float="true"
              >
                <PencilIcon className="h-6 w-6" />
              </button>
              <Tooltip id="EditChecklist" />
            </div>
          </div>
        ))}
      </div>
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <h2 className="text-xl font-bold mb-4">Are you sure you want to delete this checklist?</h2>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={deleteChecklist}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checklist;
